import ClassNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import * as styles from './Tooltip.css';

const cx = ClassNames.bind(styles);

/* istanbul ignore next */
class Tooltip extends Component {
    static propTypes = {
        tooltipData: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func])
            .isRequired,
        /**
         *  Content to display onClick/onMouseOver of tooltip text
         */
        children: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object])
            .isRequired,
        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */
        automationId: PropTypes.string,
        // theme: PropTypes.string,
        /**
         * If true tooltip content will display on click. By default it will open on mouseOver
         * Pass a function to get a callback on click of tooltipText
         */
        onClick: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
        /**
         * If true tooltip content will display on mouseOver
         * Pass a function to get a callback on mouseOver of tooltipText
         */
        onMouseOver: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
        /**
         * Placement for tooltip content
         */
        placement: PropTypes.oneOf(['bottom', 'top']),
        disableHideOnContentClick: PropTypes.bool,
        customTooltipTheme: PropTypes.string,
        // handleTooltipClose: PropTypes.func.isRequired,
        /**
         * HTML wrapped inside wrapper html
         * @type {[type]}
         */
        // isVisible: PropTypes.bool.isRequired,
        visibleOnLoad: PropTypes.bool,
        // This will enable dynamic positioning of tooltip content relative to tooltip text.
        dynamicPosition: PropTypes.bool,
        enableOverlay: PropTypes.bool,
        tooltipclassName: PropTypes.string,
    };

    static defaultProps = {
        automationId: null,
        onClick: false,
        onMouseOver: false,
        placement: 'bottom',
        disableHideOnContentClick: true,
        customTooltipTheme: {},
        visibleOnLoad: false,
        dynamicPosition: true,
        enableOverlay: false,
        tooltipclassName: '',
    };

    containerClicked = false;

    constructor(props) {
        super(props);
        const {
            tooltipWrapperClass,
            tooltipContentClass,
            tooltipTextClass,
        } = this.props.customTooltipTheme;

        const tooltipTheme = {
            tooltipWrapper: cx('tooltipWrapper', tooltipWrapperClass),
            tooltipContent: cx('tooltipContent', tooltipContentClass),
            tooltipTextTheme: cx('tooltipText', tooltipTextClass),
        };

        this.state = {
            isVisible: this.props.visibleOnLoad,
            contentStyle: {},
            tooltipTheme,
        };
    }

    componentDidMount() {
        this.setTooltipPosition();
    }

    /**
     * If content width is greater than body width then body width will be considered.
     */
    getTooltipPosition = () => {
        let placement = this.state.placement || this.props.placement;
        const contentStyle = {};
        try {
            const { width: bodyWidth, top: bodyTop } = document.body.getBoundingClientRect();

            const {
                width: contentWidth,
                top: contentTop,
                bottom: contentBottom,
                height: contentHeight,
            } = this.tooltipContentRef.getBoundingClientRect();

            const {
                width: tooltipTextWidth,
                top: tooltipTextTop,
                bottom: tooltipTextBottom,
                left: tooltipTextLeft,
                right: tooltipTextRight,
            } = this.tooltipTextRef.getBoundingClientRect();

            let totalHeightOfContent;
            const windowHeight =
                window.innerHeight ||
                document.documentElement.clientHeight ||
                document.body.clientHeight;

            // If content width is greater than body width then body width will be considered.
            if (contentWidth > bodyWidth) {
                contentStyle.width = bodyWidth;
                contentStyle.right = tooltipTextRight - bodyWidth;
                contentStyle.margin = 0;
            } else if (contentWidth < tooltipTextWidth) {
                contentStyle.left = 0;
            } else {
                // For center aligning tooltip content
                const tooltipHalfWidth =
                    Math.round(contentWidth / 2) - Math.round(tooltipTextWidth / 2);
                const tooltipRightSpace = bodyWidth - tooltipTextRight;
                const suffLeftSpace = tooltipHalfWidth < tooltipTextLeft;
                const suffRightSpace = tooltipHalfWidth < tooltipRightSpace;

                if (suffLeftSpace && suffRightSpace) {
                    contentStyle.left = -tooltipHalfWidth;
                } else if (!suffLeftSpace) {
                    contentStyle.left = -tooltipTextLeft;
                } else if (!suffRightSpace) {
                    delete contentStyle.left;
                    contentStyle.right = -tooltipRightSpace;
                }
            }

            if (contentTop < tooltipTextTop) {
                totalHeightOfContent = contentHeight + (contentBottom - tooltipTextTop);
                placement = tooltipTextTop - totalHeightOfContent > 0 ? 'top' : 'bottom';
            } else {
                totalHeightOfContent = contentHeight + (contentTop - tooltipTextBottom);
                placement =
                    tooltipTextBottom + totalHeightOfContent < windowHeight ? 'bottom' : 'top';
            }
            return { contentStyle, placement, scrollPosition: bodyTop };
        } catch (error) {
            /* eslint-disable no-console */
            console.log('Error in Tooltip component', error);
        }
        return { contentStyle, placement, scrollPosition: 0 };
    };

    getTooltipText = () => {
        const { children, onClick, enableOverlay } = this.props;
        const {
            isVisible,
            tooltipTheme: { tooltipTextTheme },
        } = this.state;
        const textAdditionalProps = {};
        const displayContent = () => this.toggleDisplayContent(true);
        const removeContent = () => this.toggleDisplayContent(false);

        if (onClick) {
            textAdditionalProps.onClick = () => this.toggleDisplayContent(!isVisible);
            if (!enableOverlay) {
                isVisible ? this.addOutsideClickListener() : this.removeOutsideClickListener();
            }
        } else {
            textAdditionalProps.onMouseOver = displayContent;
            textAdditionalProps.onMouseLeave = removeContent;
        }

        return (
            <button
                type="button"
                className={tooltipTextTheme}
                ref={(tooltipTextRef) => {
                    this.tooltipTextRef = tooltipTextRef;
                }}
                /* eslint-disable react/jsx-props-no-spreading */
                {...textAdditionalProps}
            >
                {children}
            </button>
        );
    };

    setActionOnContainer = () => {
        this.containerClicked = true;
    };

    setTooltipPosition = () => {
        if (this.state.isVisible && this.props.dynamicPosition) {
            const { contentStyle, placement } = this.getTooltipPosition();
            this.setState({
                contentStyle,
                placement,
            });
        }
    };

    addOutsideClickListener = () => {
        if (!__SERVER__) {
            document.addEventListener('click', this.toggleDisplayContent, false);
        }
    };

    removeOutsideClickListener = () => {
        if (!__SERVER__) {
            document.removeEventListener('click', this.toggleDisplayContent, false);
        }
    };

    toggleDisplayContent = (visibility) => {
        const { onClick, onMouseOver } = this.props;
        const { isVisible } = this.state;
        const tooltipCallback = onClick || onMouseOver;
        let toggle = true;

        if (this.containerClicked) {
            toggle = false;
            this.containerClicked = false;
        }

        if (typeof visibility !== 'boolean') {
            // eslint-disable-next-line no-param-reassign
            visibility = false;
        }

        if (toggle && visibility !== isVisible && this.tooltipWrapperRef) {
            this.setState(
                {
                    isVisible: visibility,
                    contentStyle: {},
                },
                () => {
                    if (typeof tooltipCallback === 'function') {
                        tooltipCallback(visibility);
                    }
                    this.setTooltipPosition();
                }
            );
        }
    };

    overlayClicked = () => {
        this.containerClicked = false;
        this.toggleDisplayContent(false);
    };

    render() {
        const {
            tooltipData,
            automationId,
            disableHideOnContentClick,
            onClick,
            enableOverlay,
            tooltipclassName,
        } = this.props;
        const containerProps = {};
        const {
            isVisible,
            contentStyle,
            placement,
            tooltipTheme: { tooltipWrapper, tooltipContent },
        } = this.state;

        const tooltipWrapperClass = cx(tooltipWrapper, placement, {
            displayArrow: isVisible,
            enableOverlay,
        });
        const tooltipContentClass = cx(tooltipContent, placement, {
            enableOverlay,
        });

        if (disableHideOnContentClick && onClick) {
            containerProps.onClick = this.setActionOnContainer;
        }

        return (
            /* eslint-disable jsx-a11y/control-has-associated-label */
            <div
                data-automation-id={automationId}
                ref={(tooltipWrapperRef) => {
                    this.tooltipWrapperRef = tooltipWrapperRef;
                }}
                className={tooltipWrapperClass}
            >
                {this.getTooltipText()}
                {isVisible && (
                    <div
                        className={(tooltipContentClass, tooltipclassName)}
                        style={contentStyle}
                        {...containerProps}
                        ref={(tooltipContentRef) => {
                            this.tooltipContentRef = tooltipContentRef;
                        }}
                    >
                        {typeof tooltipData === 'function' ? tooltipData() : tooltipData}
                    </div>
                )}
                {isVisible && enableOverlay && (
                    <button
                        type="button"
                        onClick={this.overlayClicked}
                        className={cx('overlayDesign', { enableOverlay })}
                    />
                )}
            </div>
        );
    }
}

export default Tooltip;
